import { Paper, Title, Box, Text, Table, ScrollArea } from "@mantine/core";

/**
 * Most-visited pages.
 *
 * Server-aggregated on the normalised PagePath, so "/pricing" and "/pricing?utm_source=x" are one
 * page. The previous version grouped on the raw URL split at "?" in the browser, over at most the
 * newest 1,000 events, and reported "unique users" from event.userId — a field only ever set for
 * signed-in visitors, so for ordinary marketing traffic that column was a column of zeroes.
 */
export default function TopPages({ pages, isLoading, compact = false }) {
  if (isLoading) {
    return (
      <Paper p="md" withBorder={!compact}>
        {!compact && <Title order={4}>Top Pages</Title>}
        <Box h={200} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text c="dimmed">Loading...</Text>
        </Box>
      </Paper>
    );
  }

  const rows = pages ?? [];

  return (
    <Paper p="md" withBorder={!compact}>
      {!compact && <Title order={4} mb="md">Top Pages</Title>}

      <ScrollArea h={compact ? 200 : 300}>
        {rows.length > 0 ? (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Page</Table.Th>
                <Table.Th ta="right">Views</Table.Th>
                <Table.Th ta="right">Sessions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((page) => (
                <Table.Tr key={page.url}>
                  <Table.Td>
                    <Text size="sm" lineClamp={1} title={page.url}>
                      {displayPath(page.url)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">{(page.visits ?? 0).toLocaleString()}</Table.Td>
                  <Table.Td ta="right">{(page.sessions ?? 0).toLocaleString()}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <Box h={200} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Text c="dimmed">No page data available</Text>
          </Box>
        )}
      </ScrollArea>
    </Paper>
  );
}

/** Show the path rather than the full URL — every row shares the same origin. */
function displayPath(url) {
  if (!url) return "—";
  try {
    const parsed = new URL(url);
    return (parsed.pathname || "/") + parsed.search;
  } catch {
    return url;
  }
}
